import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';
import type { ScenePlan } from 'js/src/data/html-design';

import { canvasSizeOf } from './lint/measure';
import { HTML_DESIGN_SKILL_META } from './skills/skillMeta';

export interface BeatTypeBlock {
    beat: string;
    atSeconds: number;
    /** The settled type block's extent as shares of the frame; null when no type is on screen. */
    width: number | null;
    height: number | null;
    /** How many words the block reads as; a lone word fitted to the width has nothing to stack. */
    words?: number;
    /** Whether a canvas is drawing at that moment, which can carry a word no DOM text holds. */
    drawsOnCanvas?: boolean;
}

export function isEmptyBeat({ width, drawsOnCanvas }: BeatTypeBlock): boolean {
    return width === null && !drawsOnCanvas;
}

const REST_LEAD_SECONDS = 0.12;
/** Type this much smaller than the largest on screen is a stem or a label, not the block. */
const BLOCK_SHARE_OF_LARGEST = 0.4;
const OPAQUE_ENOUGH = 0.5;

export function isUnderScale({ width, height, words }: BeatTypeBlock): boolean {
    if (width === null || height === null) {
        return false;
    }
    const fittedToWidth = width >= 0.7;
    if (!fittedToWidth) {
        return height < 0.45;
    }
    return height < 0.3 && (words ?? 2) > 1;
}

const percent = (share: number) => `${Math.round(share * 100)}%`;

export function emptyBeatMessage(blocks: readonly BeatTypeBlock[]): string | null {
    const empty = blocks.filter(isEmptyBeat);
    if (empty.length === 0) {
        return null;
    }
    const beats = empty
        .map((block) => `"${block.beat}" (settled at ${block.atSeconds.toFixed(1)}s)`)
        .join(', ');
    return (
        `Nothing readable is on screen once ${empty.length === 1 ? 'this beat has' : 'these beats have'} ` +
        `settled: ${beats}. Its word is hidden, never arrives, or has already left. Check that the ` +
        `copies a technique builds are visible (a clone copies the original's inline styles, ` +
        `\`visibility: hidden\` included) and that the entrance lands before the next beat.`
    );
}

export function underScaleMessage(blocks: readonly BeatTypeBlock[]): string | null {
    const small = blocks.filter(isUnderScale);
    if (small.length === 0) {
        return null;
    }
    const beats = small
        .map(
            (block) =>
                `"${block.beat}" (settled at ${block.atSeconds.toFixed(1)}s) spans ` +
                `${percent(block.width ?? 0)} of the frame's width and ${percent(block.height ?? 0)} ` +
                `of its height`,
        )
        .join('; ');
    return (
        `Kinetic type fills the frame, and ${small.length === 1 ? 'this beat does' : 'these beats do'} ` +
        `not: ${beats}. Size every line with fitWord to 80–90% of the frame's width rather than a ` +
        `fixed font-size, and when one line is still short of half the frame's height, stack the ` +
        `words around it into a justified block, its lines fitted together in one fitWord call, with ` +
        `long words in the condensed face.`
    );
}

export function usesKineticType(techniques: readonly string[]): boolean {
    return techniques.some((name) => HTML_DESIGN_SKILL_META[name]?.category === 'kinetic-type');
}

function restPoints(plan: ScenePlan, seconds: number): { beat: string; at: number }[] {
    return plan.phases.map((phase, index) => {
        const next = plan.phases[index + 1]?.at ?? seconds;
        return {
            beat: phase.beat,
            at: Math.min(seconds, Math.max(phase.at, next - REST_LEAD_SECONDS)),
        };
    });
}

function visible(element: Element, view: Window): boolean {
    let opacity = 1;
    for (let node: Element | null = element; node; node = node.parentElement) {
        const style = view.getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
        }
        opacity *= Number(style.opacity || 1);
    }
    return opacity >= OPAQUE_ENOUGH;
}

function drawsOnCanvas(document: Document): boolean {
    const view = document.defaultView;
    return Boolean(
        view &&
        Array.from(document.querySelectorAll('canvas')).some((canvas) => visible(canvas, view)),
    );
}

const NOT_DRAWN_SCALE = 0.05;

function transformScale(transform: string): { x: number; y: number } {
    const values = /matrix(3d)?\(([^)]*)\)/.exec(transform)?.[2]?.split(',').map(Number);
    if (!values) {
        return { x: 1, y: 1 };
    }
    const [a, b, c, d] = transform.includes('matrix3d')
        ? [values[0], values[1], values[4], values[5]]
        : values;
    return { x: Math.hypot(a, b), y: Math.hypot(c, d) };
}

function scaleProperty(scale: string): { x: number; y: number } {
    const [x, y = x] = scale.split(/\s+/).map(Number);
    return Number.isFinite(x) ? { x, y } : { x: 1, y: 1 };
}

/**
 * How much the element is drawn scaled by its own and its ancestors' transforms. A technique that
 * scales a fitted word is the motion working; the check sizes the type as it was set.
 */
export function appliedScale(element: Element, view: Window): { x: number; y: number } {
    let x = 1;
    let y = 1;
    for (let node: Element | null = element; node; node = node.parentElement) {
        const style = view.getComputedStyle(node);
        const matrix = transformScale(style.transform || 'none');
        const property = scaleProperty(style.scale || 'none');
        x *= matrix.x * property.x;
        y *= matrix.y * property.y;
    }
    return { x, y };
}

/**
 * The words the block's elements spell, read in document order: letters set one per element run
 * together into one word, and whitespace or anything outside the block separates them.
 */
export function countBlockWords(document: Document, block: readonly Element[]): number {
    const inBlock = new Set(block);
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let text = '';
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        text += node.parentElement && inBlock.has(node.parentElement) ? node.textContent : ' ';
    }
    return text.split(/\s+/).filter(Boolean).length;
}

interface TextBox {
    element: Element;
    drawn: DOMRect;
    scale: { x: number; y: number };
}

function median(values: number[]): number {
    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
}

/**
 * The union of the large type on screen: what reads as the block, without the stem or labels.
 *
 * Its extent is the larger of the block as drawn and as set before the technique scaled it, so a
 * fitted word a technique shrinks and a small one it grows to fill the frame both count as filling
 * it. Only type that is small both ways falls short.
 */
export function measureTypeBlock(
    document: Document,
): { width: number; height: number; words: number } | null {
    const view = document.defaultView;
    if (!view) {
        return null;
    }
    const canvas = canvasSizeOf(document);
    const boxes: TextBox[] = [];
    for (const element of Array.from(document.body.querySelectorAll('*'))) {
        const text = Array.from(element.childNodes).filter(
            (node) => node.nodeType === node.TEXT_NODE && node.textContent?.trim(),
        );
        if (text.length === 0 || !visible(element, view)) {
            continue;
        }
        const range = document.createRange();
        range.setStartBefore(text[0]);
        range.setEndAfter(text[text.length - 1]);
        const drawn = range.getBoundingClientRect();
        const scale = appliedScale(element, view);
        if (drawn.width > 0 && drawn.height > 0 && Math.min(scale.x, scale.y) >= NOT_DRAWN_SCALE) {
            boxes.push({ element, drawn, scale });
        }
    }
    if (boxes.length === 0) {
        return null;
    }
    const setHeight = ({ drawn, scale }: TextBox) => drawn.height / scale.y;
    const largest = Math.max(...boxes.map(setHeight));
    const block = boxes.filter((box) => setHeight(box) >= largest * BLOCK_SHARE_OF_LARGEST);
    const left = Math.min(...block.map(({ drawn }) => drawn.left));
    const right = Math.max(...block.map(({ drawn }) => drawn.right));
    const top = Math.min(...block.map(({ drawn }) => drawn.top));
    const bottom = Math.max(...block.map(({ drawn }) => drawn.bottom));
    const onCanvas = (from: number, to: number, size: number) =>
        Math.max(0, Math.min(size, to) - Math.max(0, from)) / size;
    const setWidth = (right - left) / median(block.map(({ scale }) => scale.x)) / canvas.width;
    const setTall = (bottom - top) / median(block.map(({ scale }) => scale.y)) / canvas.height;
    return {
        width: Math.max(onCanvas(left, right, canvas.width), Math.min(1, setWidth)),
        height: Math.max(onCanvas(top, bottom, canvas.height), Math.min(1, setTall)),
        words: countBlockWords(
            document,
            block.map(({ element }) => element),
        ),
    };
}

/**
 * The settled type block at every beat of a kinetic-type graphic, measured on the loaded
 * composition. Null when the scene uses no kinetic-type technique, has no plan to read its beats
 * from, or has no loaded composition to measure.
 */
export async function measureKineticBeats(
    asset: HtmlAsset,
    plan: ScenePlan | null,
    techniques: readonly string[],
    sceneDurationMs: number,
): Promise<BeatTypeBlock[] | null> {
    if (!plan?.phases.length || !usesKineticType(techniques) || !asset.html) {
        return null;
    }
    const seconds = sceneDurationMs / 1000;
    const points = restPoints(plan, seconds);
    const blocks = await asset.introspectComposition(async (composition) => {
        const measured: BeatTypeBlock[] = [];
        for (const point of points) {
            const posed = (await composition.seek(point.at / seconds)) ?? composition.document;
            const block = measureTypeBlock(posed);
            measured.push({
                beat: point.beat,
                atSeconds: point.at,
                width: block?.width ?? null,
                height: block?.height ?? null,
                words: block?.words,
                drawsOnCanvas: drawsOnCanvas(posed),
            });
        }
        return measured;
    });
    return blocks ?? null;
}
